import * as Sentry from '@sentry/react';

import { useEffect, useRef } from 'react';

import { useCurrentUser } from '@/contexts/CurrentUserContext';
import { CurrentUserProfile } from '@/types/GraphqlSchema';

interface WatermarkElement extends HTMLDivElement {
  hasAttribute: (name: string) => boolean;
  querySelector: (selector: string) => Element | null;
}

const warn = (userId?: string) => {
  const message = '⚠️ Watermark removal detected! This action may be logged for security purposes.';
  console.warn(message);

  // Log to Sentry with user context
  Sentry.captureMessage(message, {
    level: 'warning',
    tags: {
      component: 'watermark',
      action: 'removal_detected',
    },
    user: {
      id: userId || 'unknown',
    },
    extra: {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    },
  });
};

// Function to create a protected watermark element
const createProtectedWatermark = (currentUser: CurrentUserProfile): WatermarkElement => {
  const userName =
    currentUser?.name || `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
  const clientName = currentUser?.company || 'Welligence';
  const watermark = document.createElement('div') as WatermarkElement;
  watermark.style.cssText = `
      position: absolute !important;
      bottom: 2px !important;
      left: 4px !important;
      z-index: 999999 !important;
      pointer-events: none !important;
      font-size: 0.6rem !important;
      color: #666 !important;
      font-family: monospace !important;
      white-space: nowrap !important;
      user-select: none !important;
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
      opacity: 0.8 !important;
    `;
  watermark.textContent = `For the exclusive use of ${userName} at ${clientName}`;
  watermark.setAttribute('data-watermark', 'true');
  watermark.setAttribute('data-protected', 'true');
  watermark.setAttribute('data-tamper-proof', 'true');

  // Add event listeners to prevent removal
  watermark.addEventListener('DOMNodeRemoved', () => {
    warn(currentUser?.id);
  });

  return watermark;
};

const addWatermarkToAllCards = (currentUser: CurrentUserProfile, isRestore?: boolean) => {
  const cards = document.querySelectorAll('.smart-report-page');
  cards.forEach((card: Element) => {
    const htmlCard = card as HTMLElement;
    if (!htmlCard.querySelector('[data-watermark="true"]')) {
      if (isRestore) warn(currentUser?.id);
      const watermarkElement = createProtectedWatermark(currentUser);
      htmlCard.style.position = 'relative';
      htmlCard.appendChild(watermarkElement);
    }
  });
};

export const Watermark = () => {
  const { currentUser } = useCurrentUser();
  const observerRef = useRef<MutationObserver | null>(null);
  const restoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Add watermarks to all existing cards
    addWatermarkToAllCards(currentUser);

    // Set up mutation observer to detect new cards and removed watermarks
    observerRef.current = new MutationObserver((mutations: MutationRecord[]) => {
      let shouldRestore = false;

      mutations.forEach((mutation: MutationRecord) => {
        if (mutation.type === 'childList') {
          // Check for removed watermarks
          mutation.removedNodes.forEach((node: Node) => {
            if (
              node.nodeType === Node.ELEMENT_NODE &&
              ((node as WatermarkElement).hasAttribute('data-watermark') ||
                (node as WatermarkElement).querySelector('[data-watermark="true"]'))
            ) {
              shouldRestore = true;
            }
          });

          // Check for new cards added
          mutation.addedNodes.forEach((node: Node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              if (
                element.classList?.contains('smart-report-page') ||
                element.querySelector?.('.smart-report-page')
              ) {
                // New card detected, add watermarks to all cards
                setTimeout(() => addWatermarkToAllCards(currentUser), 100);
              }
            }
          });
        }
      });

      if (shouldRestore) {
        warn(currentUser?.id);
        setTimeout(() => addWatermarkToAllCards(currentUser, true), 100);
      }
    });

    // Start observing the entire document for changes
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Periodic check as backup
    const intervalId = setInterval(() => addWatermarkToAllCards(currentUser, true), 3000);

    // Prevent right-click context menu
    const handleContextMenu = (e: MouseEvent): boolean => {
      const target = e.target as HTMLElement;
      if (target.hasAttribute('data-watermark')) {
        e.preventDefault();
        return false;
      }
      return true;
    };

    document.addEventListener('contextmenu', handleContextMenu);

    // Store timeout reference for cleanup
    const timeoutRef = restoreTimeoutRef.current;

    // Cleanup function
    return (): void => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
      clearInterval(intervalId);
      document.removeEventListener('contextmenu', handleContextMenu);

      // Remove all watermarks on cleanup
      const watermarks = document.querySelectorAll('[data-watermark="true"]');
      watermarks.forEach((watermark: Element) => {
        if (watermark.parentNode) {
          watermark.parentNode.removeChild(watermark);
        }
      });
    };
  }, [currentUser?.id]);

  // This component doesn't render anything visible since we're using direct DOM manipulation
  return null;
};
